import axios from 'axios';
import { API_BASE_URL } from '../config/apiBaseUrl';
import { User, Invoice, LoginCredentials, SignupData, AuthResponse, Hostel, Floor, Room, Bed, TenantOnboarding, TenantLead, DailyGuest, Complaint, Expense, HostelDocument, Booking, Payment, NotificationLog, CreditPackage } from '../types';

const API_URL = API_BASE_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Public API (no auth redirect on 401). */
export const publicApi = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-access-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // avoid redirect loops if already on login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const publicOnboardService = {
  getJoinHostel: async (
    hostelCode: string,
  ): Promise<{ id: number; name: string; hostelCode?: string; city?: string; address?: string }> => {
    const response = await publicApi.get(`/api/public/join/${encodeURIComponent(hostelCode)}`);
    return response.data.hostel;
  },

  submitApplication: async (hostelCode: string, formData: FormData): Promise<{ message: string }> => {
    const response = await publicApi.post(`/api/public/join/${encodeURIComponent(hostelCode)}/apply`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export const authService = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/signin', credentials);
    return response.data;
  },

  signup: async (data: SignupData): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/signup', data);
    return response.data;
  },

  refreshToken: async (): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/refresh-token', {});
    return response.data;
  },

  getCurrentUser: async (): Promise<{ user: User }> => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  getAccessibleHostels: async (): Promise<{
    hostelIds: number[];
    hostels: Array<{ id: number; name: string; status?: string; isDeleted?: boolean }>;
  }> => {
    const response = await api.get('/api/auth/accessible-hostels');
    return response.data;
  },

  switchHostel: async (hostelId: number): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/switch-hostel', { hostelId });
    return response.data;
  },
};

export type TenantProfileEditable = {
  email: boolean;
  address: boolean;
  workingPlace: boolean;
  emergencyContactName: boolean;
  emergencyContactPhoneNumber: boolean;
  dob: boolean;
  photo: boolean;
  idFrontSide: boolean;
  idBackSide: boolean;
  canEditAny: boolean;
};

export type TenantLocationInfo = {
  floorNumber: string | null;
  roomNumber: string | null;
  bedNumber: string | null;
  display: string;
};

export type GuestStayInfo = {
  id: number;
  hostelId: number;
  checkInDate: string;
  checkOutDate: string;
  status: string;
  comments?: string;
  days: number;
  dailyRate: number;
  amountPaid: number;
  totalDue: number;
  balanceDue: number;
  location: TenantLocationInfo;
};

export type TenantAccountPayload = {
  user: User;
  editable: TenantProfileEditable;
  location: TenantLocationInfo;
  accountKind?: 'tenant' | 'guest';
  guestStay?: GuestStayInfo | null;
  hostel: Pick<
    Hostel,
    | 'id'
    | 'name'
    | 'description'
    | 'address'
    | 'city'
    | 'state'
    | 'pincode'
    | 'country'
    | 'phone'
    | 'email'
    | 'logo'
    | 'websiteUrl'
    | 'hostelRules'
  >;
};

function mapAccountResponse(data: Record<string, unknown>): TenantAccountPayload {
  return {
    user: data.user as User,
    editable: data.editable as TenantProfileEditable,
    location: data.location as TenantLocationInfo,
    hostel: data.hostel as TenantAccountPayload['hostel'],
    accountKind: data.accountKind as TenantAccountPayload['accountKind'],
    guestStay: (data.guestStay as GuestStayInfo | null | undefined) ?? null,
  };
}

export const meService = {
  getAccount: async (): Promise<TenantAccountPayload> => {
    const response = await api.get('/api/me/account');
    return mapAccountResponse(response.data);
  },

  updateProfile: async (formData: FormData): Promise<TenantAccountPayload> => {
    const response = await api.patch('/api/me/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return mapAccountResponse(response.data);
  },

  getGuestStays: async (): Promise<GuestStayInfo[]> => {
    const response = await api.get('/api/me/guest-stays');
    return response.data.stays ?? [];
  },

  getMyInvoices: async (): Promise<Invoice[]> => {
    const response = await api.get('/api/me/invoices');
    return response.data ?? [];
  },

  payGuestStay: async (amount: number): Promise<TenantAccountPayload & { message: string }> => {
    const response = await api.post('/api/me/guest/pay', { amount });
    return { ...mapAccountResponse(response.data), message: response.data.message };
  },

  reCheckInGuest: async (payload: {
    checkInDate: string;
    checkOutDate: string;
    dailyRate?: number;
    floorId?: number | null;
    roomId?: number | null;
    bedId?: number | null;
  }): Promise<TenantAccountPayload & { message: string }> => {
    const response = await api.post('/api/me/guest/re-check-in', payload);
    return { ...mapAccountResponse(response.data), message: response.data.message };
  },
};

export const userService = {
  getAllUsers: async (options?: { includeDeleted?: boolean }): Promise<User[]> => {
    const response = await api.get('/api/user/userList', {
      params: options?.includeDeleted ? { includeDeleted: 'true' } : undefined,
    });
    return response.data;
  },

  createSuperAdminAccount: async (data: {
    username: string;
    phoneNumber: string;
    password: string;
    email?: string;
  }): Promise<User> => {
    const response = await api.post('/api/user/super-admin-accounts', data);
    return response.data.user;
  },

  updateSuperAdminAccount: async (
    id: number,
    data: Partial<Pick<User, 'username' | 'phoneNumber'>> & { email?: string | null }
  ): Promise<User> => {
    const response = await api.put(`/api/user/super-admin-accounts/${id}`, data);
    return response.data.user;
  },

  setSuperAdminActive: async (id: number, isActive: boolean): Promise<User> => {
    const response = await api.patch(`/api/user/super-admin-accounts/${id}/active`, { isActive });
    return response.data.user;
  },

  deleteSuperAdminAccount: async (id: number): Promise<void> => {
    await api.delete(`/api/user/super-admin-accounts/${id}`);
  },

  restoreSuperAdminAccount: async (id: number): Promise<User> => {
    const response = await api.patch(`/api/user/super-admin-accounts/${id}/restore`);
    return response.data.user;
  },

  getUserById: async (id: number): Promise<User> => {
    const response = await api.get(`/api/user/userList/${id}`);
    return response.data;
  },

  getUserByPhone: async (phoneNumber: string): Promise<User> => {
    const response = await api.get(`/api/user/phone/${phoneNumber}`);
    return response.data;
  },

  updateUser: async (id: number, data: Partial<User>): Promise<User> => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      const value = data[key as keyof User];
      if (value !== undefined && value !== null) {
        if (key === 'photo' || key === 'idFrontSide' || key === 'idBackSide') {
          if (value instanceof File) {
            formData.append(key, value);
          }
        } else {
          formData.append(key, String(value));
        }
      }
    });

    const response = await api.put(`/api/user/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.user;
  },

  changePassword: async (id: number, password: string): Promise<void> => {
    await api.put(`/api/user/changePassword/${id}`, { password });
  },

  onboardUser: async (data: Partial<User>): Promise<void> => {
    await api.post('/api/user/onboardUser', data);
  },
};

export const hostelService = {
  createHostel: async (data: Partial<Hostel>): Promise<Hostel> => {
    const response = await api.post('/api/hostels', data);
    return response.data.hostel;
  },

  getAllHostels: async (options?: { includeDeleted?: boolean }): Promise<Hostel[]> => {
    const response = await api.get('/api/hostels', {
      params: options?.includeDeleted ? { includeDeleted: 'true' } : undefined,
    });
    return response.data.hostels;
  },

  getHostelById: async (id: number): Promise<Hostel> => {
    const response = await api.get(`/api/hostels/${id}`);
    return response.data.hostel;
  },

  updateHostel: async (id: number, data: Partial<Hostel>): Promise<Hostel> => {
    const response = await api.put(`/api/hostels/${id}`, data);
    return response.data.hostel;
  },

  deleteHostel: async (id: number): Promise<void> => {
    await api.delete(`/api/hostels/${id}`);
  },

  restoreHostel: async (id: number): Promise<Hostel> => {
    const response = await api.post(`/api/hostels/${id}/restore`);
    return response.data.hostel;
  },

  getHostelUsers: async (hostelId: number): Promise<User[]> => {
    const response = await api.get(`/api/hostels/${hostelId}/users`);
    return response.data.users;
  },

  getHostelTeamMembers: async (hostelId: number): Promise<User[]> => {
    const response = await api.get(`/api/hostels/${hostelId}/team`);
    return response.data.users;
  },

  getHostelUserCount: async (hostelId: number): Promise<number> => {
    const response = await api.get(`/api/hostels/${hostelId}/user-count`);
    return response.data.count;
  },

  resetHostelUserPassword: async (hostelId: number, userId: number, password: string): Promise<void> => {
    await api.put(`/api/hostels/${hostelId}/users/${userId}/password`, { password });
  },

  getHostelAnalytics: async (id: number): Promise<any> => {
    const response = await api.get(`/api/hostels/${id}/analytics`);
    return response.data.analytics;
  },

  uploadHostelLogo: async (id: number, file: File): Promise<Hostel> => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.put(`/api/hostels/${id}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.hostel;
  },

  assignHostelsToUser: async (payload: {
    hostelIds: number[];
    phoneNumber?: string;
    userId?: number;
  }): Promise<{ message: string; user: User; hostelIds: number[] }> => {
    const response = await api.post('/api/hostels/assign-to-user', payload);
    return response.data;
  },
};

export const subscriptionService = {
  getHostelSubscription: async (hostelId: number): Promise<any> => {
    const response = await api.get(`/api/hostels/${hostelId}/subscription`);
    return response.data;
  },
  updateHostelSubscription: async (hostelId: number, data: any): Promise<any> => {
    const response = await api.put(`/api/hostels/${hostelId}/subscription`, data);
    return response.data;
  },
  setHostelStatus: async (hostelId: number, status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'): Promise<any> => {
    const response = await api.put(`/api/hostels/${hostelId}/status`, { status });
    return response.data;
  },
};

export const settingsService = {
  getBillingRules: async (
    hostelId: number,
  ): Promise<{
    billingDueDay: number;
    paymentRemindersEnabled: boolean;
    reminderStartDay: number;
    reminderEndDay: number;
    reminderCadenceDays: number;
    reminderRunHour: number;
    reminderRunMinute: number;
    lateFeePerDay: number;
    lateFeeCap: number;
  }> => {
    const response = await api.get(`/api/hostels/${hostelId}/billing-rules`);
    return response.data.rules;
  },
  updateBillingRules: async (
    hostelId: number,
    rules: Partial<{
      billingDueDay: number;
      paymentRemindersEnabled: boolean;
      reminderStartDay: number;
      reminderEndDay: number;
      reminderCadenceDays: number;
      reminderRunHour: number;
      reminderRunMinute: number;
      lateFeePerDay: number;
      lateFeeCap: number;
    }>,
  ): Promise<{
    billingDueDay: number;
    paymentRemindersEnabled: boolean;
    reminderStartDay: number;
    reminderEndDay: number;
    reminderCadenceDays: number;
    reminderRunHour: number;
    reminderRunMinute: number;
    lateFeePerDay: number;
    lateFeeCap: number;
  }> => {
    const response = await api.put(`/api/hostels/${hostelId}/billing-rules`, rules);
    return response.data.rules;
  },
  getTenantDefaults: async (
    hostelId: number
  ): Promise<{ defaultNewTenantPassword: string; hasDefault: boolean; defaultNewTenantAdvance: number }> => {
    const response = await api.get(`/api/hostels/${hostelId}/settings/tenant-defaults`);
    return response.data.defaults;
  },
  updateTenantDefaults: async (
    hostelId: number,
    payload: { defaultNewTenantPassword?: string; defaultNewTenantAdvance?: number }
  ): Promise<{ defaultNewTenantPassword: string; hasDefault: boolean; defaultNewTenantAdvance: number }> => {
    const response = await api.put(`/api/hostels/${hostelId}/settings/tenant-defaults`, payload);
    return response.data.defaults;
  },
  getHostelRules: async (hostelId: number): Promise<{ hostelRules: string }> => {
    const response = await api.get(`/api/hostels/${hostelId}/settings/hostel-rules`);
    return response.data.rules;
  },
  updateHostelRules: async (hostelId: number, payload: { hostelRules: string }): Promise<{ hostelRules: string }> => {
    const response = await api.put(`/api/hostels/${hostelId}/settings/hostel-rules`, payload);
    return response.data.rules;
  },
  getDisplayWidgets: async (hostelId: number): Promise<Record<string, boolean>> => {
    const response = await api.get(`/api/hostels/${hostelId}/settings/display-widgets`);
    return response.data.widgets;
  },
  updateDisplayWidgets: async (
    hostelId: number,
    widgets: Record<string, boolean>
  ): Promise<Record<string, boolean>> => {
    const response = await api.put(`/api/hostels/${hostelId}/settings/display-widgets`, { widgets });
    return response.data.widgets;
  },
};

export const bookingService = {
  createBooking: async (data: Partial<Booking>): Promise<Booking> => {
    const response = await api.post('/api/bookings', data);
    return response.data.booking;
  },
  getBookingsByHostel: async (hostelId: number, status?: string): Promise<Booking[]> => {
    const qs = status ? `?status=${status}` : '';
    const response = await api.get(`/api/hostels/${hostelId}/bookings${qs}`);
    return response.data.bookings;
  },
  cancelBooking: async (id: number): Promise<Booking> => {
    const response = await api.post(`/api/bookings/${id}/cancel`, {});
    return response.data.booking;
  }
  ,
  checkinBooking: async (id: number): Promise<Booking> => {
    const response = await api.post(`/api/bookings/${id}/checkin`, {});
    return response.data.booking;
  }
};

export const floorService = {
  createFloor: async (data: Partial<Floor>): Promise<Floor> => {
    const response = await api.post('/api/floors', data);
    return response.data.floor;
  },

  getFloorsByHostel: async (hostelId: number): Promise<Floor[]> => {
    const response = await api.get(`/api/hostels/${hostelId}/floors`);
    return response.data.floors;
  },

  updateFloor: async (id: number, data: Partial<Floor>): Promise<Floor> => {
    const response = await api.put(`/api/floors/${id}`, data);
    return response.data.floor;
  },

  deleteFloor: async (
    id: number
  ): Promise<{ message?: string; removedRooms?: number; removedBeds?: number }> => {
    const response = await api.delete(`/api/floors/${id}`);
    return response.data;
  },
};

export const roomService = {
  createRoom: async (data: Partial<Room>): Promise<Room> => {
    const response = await api.post('/api/rooms', data);
    return response.data.room;
  },

  getRoomsByHostel: async (hostelId: number): Promise<Room[]> => {
    const response = await api.get(`/api/hostels/${hostelId}/rooms`);
    return response.data.rooms;
  },

  updateRoom: async (id: number, data: Partial<Room>): Promise<Room> => {
    const response = await api.put(`/api/rooms/${id}`, data);
    return response.data.room;
  },

  deleteRoom: async (id: number): Promise<{ message?: string; removedBeds?: number }> => {
    const response = await api.delete(`/api/rooms/${id}`);
    return response.data;
  },
};

export const bedService = {
  getBedsByRoom: async (roomId: number): Promise<Bed[]> => {
    const response = await api.get(`/api/rooms/${roomId}/beds`);
    return response.data.beds;
  },

  getBedsByHostel: async (hostelId: number): Promise<Bed[]> => {
    const response = await api.get(`/api/hostels/${hostelId}/beds`);
    return response.data.beds;
  },

  updateBedStatus: async (id: number, bedStatus: string, tenantId?: number): Promise<Bed> => {
    const response = await api.put(`/api/beds/${id}/status`, { bedStatus, tenantId });
    return response.data.bed;
  },

  updateBed: async (
    id: number,
    data: Partial<Pick<Bed, 'bedNumber' | 'bedName' | 'rentPerDay' | 'rentPerWeek' | 'rentPerMonth'>>
  ): Promise<Bed> => {
    const response = await api.put(`/api/beds/${id}`, data);
    return response.data.bed;
  },

  deleteBed: async (id: number): Promise<void> => {
    await api.delete(`/api/beds/${id}`);
  },
};

export const tenantService = {
  createOnboarding: async (data: Partial<TenantOnboarding>): Promise<TenantOnboarding> => {
    const response = await api.post('/api/tenants/onboarding', data);
    return response.data.onboarding;
  },

  getOnboardingRecords: async (hostelId: number, status?: string): Promise<TenantOnboarding[]> => {
    const params = status ? `?status=${status}` : '';
    const response = await api.get(`/api/hostels/${hostelId}/onboardings${params}`);
    return response.data.records;
  },

  getOnboardingById: async (id: number): Promise<TenantOnboarding> => {
    const response = await api.get(`/api/onboardings/${id}`);
    return response.data.onboarding;
  },

  updateOnboarding: async (id: number, data: Partial<TenantOnboarding>): Promise<TenantOnboarding> => {
    const response = await api.put(`/api/onboardings/${id}`, data);
    return response.data.onboarding;
  },

  approveOnboarding: async (
    id: number,
    data: {
      kycVerified?: boolean;
      bedId: number;
      rent?: number | string;
      advance?: number | string;
      dateOfJoining: string;
      paymentDate: string;
      address?: string;
      workingPlace?: string;
      emergencyContactName?: string;
      emergencyContactPhoneNumber?: string;
    },
  ): Promise<TenantOnboarding> => {
    const response = await api.post(`/api/onboardings/${id}/approve`, data);
    return response.data.onboarding;
  },

  rejectOnboarding: async (id: number, rejectionReason: string): Promise<TenantOnboarding> => {
    const response = await api.post(`/api/onboardings/${id}/reject`, { rejectionReason });
    return response.data.onboarding;
  },

  revertOnboardingToPending: async (id: number): Promise<TenantOnboarding> => {
    const response = await api.post(`/api/onboardings/${id}/revert-pending`, {});
    return response.data.onboarding;
  },

  deleteOnboarding: async (id: number): Promise<void> => {
    await api.delete(`/api/onboardings/${id}`);
  },

  getOnboardingStats: async (hostelId: number): Promise<any> => {
    const response = await api.get(`/api/hostels/${hostelId}/onboarding-stats`);
    return response.data.stats;
  },

  getLeadsByHostel: async (hostelId: number): Promise<TenantLead[]> => {
    const response = await api.get(`/api/hostels/${hostelId}/leads`);
    return response.data.records;
  },

  createLead: async (
    hostelId: number,
    data: { name: string; phoneNumber: string; comments?: string },
  ): Promise<TenantLead> => {
    const response = await api.post(`/api/hostels/${hostelId}/leads`, data);
    return response.data.lead;
  },

  updateLead: async (
    id: number,
    data: { name?: string; phoneNumber?: string; comments?: string },
  ): Promise<TenantLead> => {
    const response = await api.put(`/api/leads/${id}`, data);
    return response.data.lead;
  },

  deleteLead: async (id: number): Promise<void> => {
    await api.delete(`/api/leads/${id}`);
  },

  getDailyGuestsByHostel: async (
    hostelId: number,
    filters?: { date?: string; status?: string },
  ): Promise<DailyGuest[]> => {
    const params = new URLSearchParams();
    if (filters?.date) params.append('date', filters.date);
    if (filters?.status) params.append('status', filters.status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/api/hostels/${hostelId}/daily-guests${qs}`);
    return response.data.records;
  },

  createDailyGuest: async (
    hostelId: number,
    data: Partial<DailyGuest> | FormData,
  ): Promise<DailyGuest & { message?: string }> => {
    const isForm = data instanceof FormData;
    const response = await api.post(`/api/hostels/${hostelId}/daily-guests`, data, isForm
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined);
    return { ...response.data.guest, message: response.data.message };
  },

  updateDailyGuest: async (id: number, data: Partial<DailyGuest> | FormData): Promise<DailyGuest> => {
    const isForm = data instanceof FormData;
    const response = await api.put(`/api/daily-guests/${id}`, data, isForm
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined);
    return response.data.guest;
  },

  deleteDailyGuest: async (id: number): Promise<void> => {
    await api.delete(`/api/daily-guests/${id}`);
  },

  convertDailyGuestToTenant: async (
    guestId: number,
    data: {
      bedId: number;
      rent?: number;
      advance?: number;
      dateOfJoining?: string;
      paymentDate?: string;
      note?: string;
    },
  ): Promise<{ message: string; tenant: User; guest: DailyGuest }> => {
    const response = await api.post(`/api/daily-guests/${guestId}/convert-to-tenant`, data);
    return response.data;
  },

  getTenantsByHostel: async (
    hostelId: number,
    status: 'ACTIVE' | 'INACTIVE' | 'VACATING' | 'ALL' | 'ALL_USERS' | 'DELETED' = 'ACTIVE'
  ): Promise<User[]> => {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await api.get(`/api/hostels/${hostelId}/tenants${params}`);
    return response.data.tenants;
  },

  lookupTenantByPhone: async (
    hostelId: number,
    phone: string,
  ): Promise<{
    found: boolean;
    source?: string;
    alreadyInHostel?: boolean;
    activeAtThisHostel?: boolean;
    blockPrefill?: boolean;
    existingUserId?: number | null;
    hint?: string;
    profile?: {
      phoneNumber?: string;
      username?: string;
      email?: string;
      address?: string;
      workingPlace?: string;
      emergencyContactName?: string;
      emergencyContactPhoneNumber?: string;
      photo?: string;
      idFrontSide?: string;
      idBackSide?: string;
    } | null;
  }> => {
    const response = await api.get(
      `/api/hostels/${hostelId}/tenants/lookup?phone=${encodeURIComponent(phone)}`,
    );
    return response.data;
  },

  checkoutTenant: async (userId: number): Promise<User> => {
    const response = await api.post(`/api/tenants/${userId}/checkout`, {});
    return response.data.tenant;
  },

  /** Set vacating notice (notify + vacating dates) or clear it with `{ clear: true }`. */
  updateTenantNotice: async (
    hostelId: number,
    userId: number,
    data: { clear?: boolean } | { notifiedDate: string; leavingDate: string }
  ): Promise<User> => {
    const response = await api.patch(`/api/hostels/${hostelId}/tenants/${userId}/notice`, data);
    return response.data.tenant;
  },

  getHostelTenant: async (hostelId: number, userId: number): Promise<User> => {
    const response = await api.get(`/api/hostels/${hostelId}/tenants/${userId}`);
    return response.data.tenant;
  },

  resetTenantPassword: async (hostelId: number, userId: number, password: string): Promise<void> => {
    await api.put(`/api/hostels/${hostelId}/tenants/${userId}/password`, { password });
  },

  /** Hostel admin: update tenant profile (same fields as create; JSON or multipart). */
  updateTenant: async (
    hostelId: number,
    userId: number,
    data:
      | FormData
      | {
          username: string;
          phoneNumber: string;
          email?: string;
          password?: string;
          rent?: number;
          advance?: number;
          dateOfJoining?: string;
          paymentDate?: string;
          bedId?: number | '';
          address?: string;
          workingPlace?: string;
          note?: string;
          dateOfLeft?: string;
          emergencyContactName?: string;
          emergencyContactPhoneNumber?: string;
        }
  ): Promise<User> => {
    if (data instanceof FormData) {
      const response = await api.patch(`/api/hostels/${hostelId}/tenants/${userId}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.tenant;
    }
    const response = await api.patch(`/api/hostels/${hostelId}/tenants/${userId}`, data);
    return response.data.tenant;
  },

  /** Hostel admin: create tenant login + optional bed assignment (JSON or multipart for KYC/photo). */
  createTenant: async (
    hostelId: number,
    data:
      | FormData
      | {
          username: string;
          phoneNumber: string;
          email?: string;
          password?: string;
          quickUser?: boolean;
          rent?: number;
          advance?: number;
          dateOfJoining?: string;
          paymentDate?: string;
          bedId?: number | '';
          address?: string;
          workingPlace?: string;
          note?: string;
          dateOfLeft?: string;
          emergencyContactName?: string;
          emergencyContactPhoneNumber?: string;
        }
  ): Promise<User> => {
    if (data instanceof FormData) {
      const response = await api.post(`/api/hostels/${hostelId}/tenants`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.tenant;
    }
    const response = await api.post(`/api/hostels/${hostelId}/tenants`, data);
    return response.data.tenant;
  },
};

export const complaintService = {
  createComplaint: async (data: Partial<Complaint> | FormData): Promise<Complaint> => {
    if (data instanceof FormData) {
      const response = await api.post('/api/complaints', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.complaint;
    }
    const response = await api.post('/api/complaints', data);
    return response.data.complaint;
  },

  getComplaintsByHostel: async (hostelId: number, status?: string, priority?: string): Promise<Complaint[]> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/api/hostels/${hostelId}/complaints${queryString}`);
    return response.data.complaints;
  },

  getMyComplaints: async (): Promise<Complaint[]> => {
    const response = await api.get('/api/my-complaints');
    return response.data.complaints;
  },

  getComplaintById: async (id: number): Promise<Complaint> => {
    const response = await api.get(`/api/complaints/${id}`);
    return response.data.complaint;
  },

  updateComplaintStatus: async (id: number, status: string, resolutionNotes?: string): Promise<Complaint> => {
    const response = await api.put(`/api/complaints/${id}/status`, { status, resolutionNotes });
    return response.data.complaint;
  },

  assignComplaint: async (id: number, assignedTo: number): Promise<Complaint> => {
    const response = await api.post(`/api/complaints/${id}/assign`, { assignedTo });
    return response.data.complaint;
  },

  addComplaintNote: async (id: number, internalNotes: string): Promise<Complaint> => {
    const response = await api.post(`/api/complaints/${id}/notes`, { internalNotes });
    return response.data.complaint;
  },

  getComplaintStats: async (hostelId: number): Promise<any> => {
    const response = await api.get(`/api/hostels/${hostelId}/complaint-stats`);
    return response.data.stats;
  },
};

export const expenseService = {
  createExpense: async (data: Partial<Expense> | FormData): Promise<Expense> => {
    if (data instanceof FormData) {
      const response = await api.post('/api/expenses', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.expense;
    }
    const response = await api.post('/api/expenses', data);
    return response.data.expense;
  },

  getExpensesByHostel: async (hostelId: number, filters?: any): Promise<Expense[]> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/api/hostels/${hostelId}/expenses${queryString}`);
    return response.data.expenses;
  },

  getExpenseById: async (id: number): Promise<Expense> => {
    const response = await api.get(`/api/expenses/${id}`);
    return response.data.expense;
  },

  updateExpense: async (id: number, data: Partial<Expense>): Promise<Expense> => {
    const response = await api.put(`/api/expenses/${id}`, data);
    return response.data.expense;
  },

  approveExpense: async (id: number): Promise<Expense> => {
    const response = await api.post(`/api/expenses/${id}/approve`, {});
    return response.data.expense;
  },

  rejectExpense: async (id: number): Promise<Expense> => {
    const response = await api.post(`/api/expenses/${id}/reject`, {});
    return response.data.expense;
  },

  getExpenseStats: async (hostelId: number, filters?: any): Promise<any> => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/api/hostels/${hostelId}/expense-stats${queryString}`);
    return response.data.stats;
  },

  deleteExpense: async (id: number): Promise<void> => {
    await api.delete(`/api/expenses/${id}`);
  },
};

export const documentService = {
  uploadDocument: async (formData: FormData): Promise<HostelDocument> => {
    const response = await api.post('/api/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.document;
  },

  getDocumentsByHostel: async (hostelId: number): Promise<HostelDocument[]> => {
    const response = await api.get(`/api/hostels/${hostelId}/documents`);
    return response.data.documents;
  },

  getDocumentById: async (id: number): Promise<HostelDocument> => {
    const response = await api.get(`/api/documents/${id}`);
    return response.data.document;
  },

  deleteDocument: async (id: number): Promise<void> => {
    await api.delete(`/api/documents/${id}`);
  },
};

export const platformService = {
  getCreditDefaults: async (): Promise<{
    defaultEmailCredits: number;
    defaultWhatsappCredits: number;
    creditPackagesEnabled: boolean;
    creditsPageEnabled: boolean;
    emailCreditsVisible: boolean;
    whatsappCreditsVisible: boolean;
  }> => {
    const response = await api.get('/api/platform/credit-defaults');
    return response.data.defaults;
  },
  updateCreditDefaults: async (payload: {
    defaultEmailCredits?: number;
    defaultWhatsappCredits?: number;
    creditPackagesEnabled?: boolean;
    creditsPageEnabled?: boolean;
    emailCreditsVisible?: boolean;
    whatsappCreditsVisible?: boolean;
  }): Promise<{
    defaultEmailCredits: number;
    defaultWhatsappCredits: number;
    creditPackagesEnabled: boolean;
    creditsPageEnabled: boolean;
    emailCreditsVisible: boolean;
    whatsappCreditsVisible: boolean;
  }> => {
    const response = await api.put('/api/platform/credit-defaults', payload);
    return response.data.defaults;
  },
  listCreditPackages: async (): Promise<CreditPackage[]> => {
    const response = await api.get('/api/platform/credit-packages');
    return response.data.packages;
  },
  createCreditPackage: async (
    payload: Omit<CreditPackage, 'id'>,
  ): Promise<CreditPackage> => {
    const response = await api.post('/api/platform/credit-packages', payload);
    return response.data.package;
  },
  updateCreditPackage: async (
    id: number,
    payload: Partial<CreditPackage>,
  ): Promise<CreditPackage> => {
    const response = await api.put(`/api/platform/credit-packages/${id}`, payload);
    return response.data.package;
  },
  deleteCreditPackage: async (id: number): Promise<void> => {
    await api.delete(`/api/platform/credit-packages/${id}`);
  },
};

export const creditsService = {
  getHostelCredits: async (
    hostelId: number,
  ): Promise<{ hostelId: number; emailCredits: number; whatsappCredits: number }> => {
    const response = await api.get(`/api/hostels/${hostelId}/credits`);
    return response.data.credits;
  },
  loadHostelCredits: async (
    hostelId: number,
    payload: { emailCreditsAdd: number; whatsappCreditsAdd: number },
  ): Promise<{ hostelId: number; emailCredits: number; whatsappCredits: number }> => {
    const response = await api.put(`/api/hostels/${hostelId}/credits`, payload);
    return response.data.credits;
  },
  getCreditShop: async (): Promise<{
    creditsPageEnabled: boolean;
    emailCreditsVisible: boolean;
    whatsappCreditsVisible: boolean;
    enabled: boolean;
    packages: CreditPackage[];
  }> => {
    const response = await api.get('/api/credit-packages/shop');
    return response.data.shop;
  },
  purchaseCreditPackage: async (
    hostelId: number,
    packageId: number,
  ): Promise<{
    credits: { hostelId: number; emailCredits: number; whatsappCredits: number };
    purchase: {
      id: number;
      packageName: string;
      emailCreditsAdded: number;
      whatsappCreditsAdded: number;
      priceInr: number;
    };
  }> => {
    const response = await api.post(`/api/hostels/${hostelId}/credits/purchase`, { packageId });
    return {
      credits: response.data.credits,
      purchase: response.data.purchase,
    };
  },
};

export const billingService = {
  generateMonthlyRentInvoices: async (
    hostelId: number,
    opts?: { month?: string; year?: string; dueDay?: number },
  ): Promise<{
    message: string;
    paymentMonth: string;
    paymentYear: string;
    created: Array<{ userId: number; invoiceId: number }>;
    skipped: Array<{ userId: number; reason: string; invoiceId?: number }>;
  }> => {
    const response = await api.post('/api/billing/generate-monthly-rent', {
      hostelId,
      month: opts?.month,
      year: opts?.year,
      dueDay: opts?.dueDay,
    });
    return response.data;
  },
};

export const invoiceService = {
  createInvoice: async (userId: number, creatorId: number, data: Partial<Invoice>): Promise<Invoice> => {
    const response = await api.post(`/api/createInvoice/${userId}/${creatorId}`, data);
    return response.data.invoice;
  },

  getUserInvoices: async (userId: number): Promise<Invoice[]> => {
    const response = await api.get(`/api/invoice/user/${userId}`);
    return response.data;
  },

  getAllInvoices: async (): Promise<Invoice[]> => {
    const response = await api.get('/api/invoice/all');
    return response.data;
  },

  getInvoiceById: async (id: number): Promise<Invoice> => {
    const response = await api.get(`/api/invoice/${id}`);
    return response.data;
  },

  updateInvoice: async (id: number, data: Partial<Invoice>): Promise<Invoice> => {
    const response = await api.put(`/api/invoice/${id}`, data);
    return response.data.updatedInvoice;
  },

  /** Pay toward an existing invoice (due payment = invoice update, no new row). */
  applyInvoicePayment: async (
    invoiceId: number,
    data: {
      dueAmount: number;
      payingAmount: string | number;
      dueId?: number | null;
      paymentTimestamp?: string;
    }
  ): Promise<{ invoice: Invoice; receiptInvoice: Invoice | null }> => {
    const response = await api.put(`/api/invoice/${invoiceId}/payment`, {
      dueAmount: data.dueAmount,
      payNow: data.payingAmount,
      payingAmount: data.payingAmount,
      dueId: data.dueId,
      paymentTimestamp: data.paymentTimestamp,
    });
    const invoice = response.data.invoice ?? response.data.updatedInvoice;
    return {
      invoice,
      receiptInvoice: response.data.receiptInvoice ?? null,
    };
  },

  downloadInvoice: async (id: number, userId: number): Promise<Blob> => {
    const response = await api.get(`/api/invoice/download/${id}/${userId}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export const dueService = {
  getDuesByHostel: async (
    hostelId: number,
    filters?: { status?: 'OVERDUE' | 'UPCOMING' | 'CURRENT' | 'ALL'; month?: string; year?: string; userId?: number }
  ): Promise<import('../types').Due[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.month) params.append('month', filters.month);
    if (filters?.year) params.append('year', filters.year);
    if (filters?.userId) params.append('userId', String(filters.userId));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/api/hostels/${hostelId}/dues${qs}`);
    return response.data.dues;
  },

  exportDuesCsv: async (hostelId: number, status: 'OVERDUE' | 'UPCOMING' | 'CURRENT' | 'ALL' = 'ALL'): Promise<Blob> => {
    const response = await api.get(`/api/hostels/${hostelId}/dues/export?status=${status}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  recalculateDueStatuses: async (hostelId: number): Promise<{ updated: number }> => {
    const response = await api.post(`/api/hostels/${hostelId}/dues/recalculate`, {});
    return response.data;
  },

  payDue: async (
    hostelId: number,
    dueId: number,
    data: {
      paidAmount: string | number;
      balanceAmount?: string | number;
      dueDate?: string;
      invoiceId?: number | null;
      paymentTimestamp?: string;
    }
  ): Promise<import('../types').Invoice> => {
    const response = await api.post(`/api/hostels/${hostelId}/dues/${dueId}/pay`, data);
    return response.data.invoice;
  },
};

export const paymentService = {
  recordPayment: async (data: {
    hostelId?: number;
    userId: number;
    invoiceId?: number;
    dueId?: number;
    amount: number;
    method?: Payment['method'];
    reference?: string;
    paidAt?: string;
    notes?: string;
  }): Promise<any> => {
    const response = await api.post('/api/payments/record', data);
    return response.data;
  },

  getPaymentsByHostel: async (hostelId: number): Promise<Payment[]> => {
    const response = await api.get(`/api/hostels/${hostelId}/payments`);
    return response.data.payments;
  },
};

export const notificationService = {
  getNotificationsByHostel: async (hostelId: number, filters?: { type?: string; status?: string }): Promise<NotificationLog[]> => {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/api/hostels/${hostelId}/notifications${qs}`);
    return response.data.notifications;
  },
};

export default api;

